// The customer book's Tag filter (spec 09 §4.1's E2-2a1 annotation; spec 16
// §3.12 item 1, TF1). It writes `tag` to the address, which the list sends as
// `tag_id`, and a saved view stores the tag's id, never its name.
//
// It offers the active tags and the archived tags an active customer still
// carries, from one `archived=in_use` read (`useTagOptions`). An archived tag
// still filters, so a shared view or a link can carry a tag the list no longer
// offers: the select then keeps an option for it, "Not in this list", so the
// filter stays visible and can be cleared, the owner filter's rule. While the
// list is still loading that option reads as resolving instead, and when the
// list read failed it says the tags could not be loaded, because "Not in this
// list" would state a fact about a list nobody read. The screen shows the
// failure's request id and Retry under the filters.

import { useTranslation } from "react-i18next";
import { Field } from "../../../../../components/field";
import { Select } from "../../../../../components/select";
import type { SalesTag } from "../../hooks/crm-queries";
import { filterTagOptions } from "./tag-options";

/** The NAME a filter label prints for the tag the address holds: the tag's
 * own name, or undefined while the list has not named it (the export waits;
 * the bar shows "Not in this list"). */
export function tagFilterName(
  tagId: string | undefined,
  tags: readonly SalesTag[] | undefined,
): string | undefined {
  if (tagId === undefined || tags === undefined) return undefined;
  return tags.find((tag) => tag.id === tagId)?.name;
}

export function TagFilter({
  value,
  tags,
  pending,
  failed = false,
  onChange,
}: {
  value: string | undefined;
  /** The `archived=in_use` read's tags, or undefined while it has not
   * answered (or failed). */
  tags: readonly SalesTag[] | undefined;
  pending: boolean;
  /** The list read failed (and has no data). */
  failed?: boolean;
  onChange: (tagId: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const options = filterTagOptions(tags ?? []);
  const known = value !== undefined && options.some((tag) => tag.id === value);
  return (
    <Field label={t("sales.customers.filterTag")}>
      <Select
        value={value ?? ""}
        data-testid="customers-tag"
        onChange={(e) => {
          onChange(e.target.value.length > 0 ? e.target.value : undefined);
        }}
      >
        <option value="">{t("sales.customers.anyTag")}</option>
        {value !== undefined && !known ? (
          <option value={value}>
            {pending
              ? t("ref.resolving")
              : failed && tags === undefined
                ? t("sales.tags.optionsFailed")
                : t("sales.common.notListed")}
          </option>
        ) : null}
        {options.map((tag) => (
          <option key={tag.id} value={tag.id}>
            {tag.status === "archived"
              ? t("sales.customers.tagArchivedOption", { name: tag.name })
              : tag.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}
